import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Ban, Clock, Copy, Crown, KeyRound, LogOut, Mail, MessageCircle, MessageCircleOff, MonitorSmartphone, Pencil, Radio, RotateCcw, ShieldCheck, Ticket, Trash2, TriangleAlert, Unlock, UserX, X,
} from 'lucide-react';
import Loader from '../Loader';
import UpProfileData from './UpProfileData';
import { Badge, Confirm, Device, Empty, Modal, Status, useWheelToPage } from './UpUi';
import { EVENTS, ago, date, dateTime, duration, titleOf } from './format';
import { SUPPORT_PROJECT } from '../../Context/SupportContext';

const copy = (text, notify) => {
    navigator.clipboard?.writeText(text).then(() => notify('Copié'), () => notify('Copie impossible', true))
}

/* ─── Fenêtres d'action ─── */

/** Premium à la main : noté dans l'historique (onglet « Premium offert ») ; ajouté → le user est notifié en direct. */
const PremiumModal = ({ user, run, onClose }) => {
    const [until, setUntil] = useState(user.premiumUntil ? user.premiumUntil.slice(0, 10) : '')
    const [note, setNote] = useState('')
    const go = (body) => run('premium', { ...body, ...(note.trim() && { note: note.trim() }) }, onClose)
    return (
        <Modal title='Premium' onClose={onClose}>
            <p className='up-text'>
                {user.premium ? <>Premium jusqu'au <b>{date(user.premiumUntil)}</b>. Les jours s'ajoutent à cette date.</> : <>Compte gratuit. Les jours partent d'aujourd'hui.</>}
                {' '}Ajouté : il reçoit une notification tout de suite (cloche + message à l'écran).
            </p>
            <label className='up-label'>Pourquoi (facultatif, noté dans l'historique)</label>
            <input className='up-input' value={note} maxLength={255} onChange={(e) => setNote(e.currentTarget.value)} placeholder='ex. geste commercial, concours, souci de paiement…' />
            <div className='up-chips'>
                {[7, 30, 90, 365].map((d) => (
                    <button key={d} className='btn cw' onClick={() => go({ days: d })}>+{d} j</button>
                ))}
                {user.premium && <button className='btn cw' onClick={() => go({ days: -7 })}>−7 j</button>}
            </div>
            <label className='up-label'>Ou une date de fin</label>
            <div className='up-inline'>
                <input type='date' className='up-input' value={until} onChange={(e) => setUntil(e.currentTarget.value)} />
                <button className='btn cs' disabled={!until} onClick={() => go({ until })}>Définir</button>
            </div>
            {user.premiumUntil && (
                <div className='up-modal-actions'>
                    <button className='btn up-danger-soft' onClick={() => go({ until: null })}>Retirer le premium</button>
                </div>
            )}
        </Modal>
    )
}

const PasswordModal = ({ pApi, user, notify, onDone, onClose }) => {
    const [password, setPassword] = useState('')
    const [link, setLink] = useState('')
    const [busy, setBusy] = useState(false)
    const sendLink = async () => {
        setBusy(true)
        try {
            setLink((await pApi.post(`/users/${user.id}/reset-link`)).link)
            notify('Lien envoyé par email')
            onDone()
        } catch (e) { notify(e.message, true) }
        setBusy(false)
    }
    const setNew = async () => {
        setBusy(true)
        try {
            await pApi.post(`/users/${user.id}/password`, { password })
            notify('Mot de passe changé, ses appareils sont déconnectés')
            onDone()
            onClose()
        } catch (e) { notify(e.message, true) }
        setBusy(false)
    }
    return (
        <Modal title='Mot de passe' onClose={onClose}>
            <p className='up-text'>Envoyer un lien de réinitialisation (valable 30 min) à <b>{user.email}</b>. Tu peux aussi copier le lien pour le lui donner toi-même.</p>
            <button className='btn cs w100' disabled={busy} onClick={sendLink}><Mail size={15} /> Envoyer le lien</button>
            {link && (
                <div className='up-link'>
                    <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
                    <button className='pay-act-btn' title='Copier' onClick={() => copy(link, notify)}><Copy size={16} color='#fff' /></button>
                </div>
            )}
            <label className='up-label mt2'>Ou définir un nouveau mot de passe (ses appareils sont déconnectés)</label>
            <div className='up-inline'>
                <input className='up-input' type='text' placeholder='8 caractères minimum' value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
                <button className='btn cs' disabled={busy || password.length < 8} onClick={setNew}>Changer</button>
            </div>
        </Modal>
    )
}

const EmailModal = ({ pApi, user, notify, onDone, onClose }) => {
    const [email, setEmail] = useState(user.email)
    const save = async () => {
        try {
            await pApi.post(`/users/${user.id}/email`, { email })
            notify('Email changé')
            onDone()
            onClose()
        } catch (e) { notify(e.message, true) }
    }
    return (
        <Modal title="Changer l'email" onClose={onClose}>
            <p className='up-text'>C'est l'identifiant de connexion : préviens-le.</p>
            <div className='up-inline'>
                <input className='up-input' type='email' value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
                <button className='btn cs' disabled={!email || email === user.email} onClick={save}>Enregistrer</button>
            </div>
        </Modal>
    )
}

const InvitesModal = ({ pApi, user, notify, onDone, onClose }) => {
    const [count, setCount] = useState(1)
    const [days, setDays] = useState(0)
    const [codes, setCodes] = useState([])
    const create = async () => {
        try {
            setCodes((await pApi.post(`/users/${user.id}/invites`, { count: Number(count), days: Number(days) })).codes)
            notify('Codes créés')
            onDone()
        } catch (e) { notify(e.message, true) }
    }
    return (
        <Modal title="Donner des codes d'invitation" onClose={onClose}>
            <p className='up-text'>Codes à son nom : les comptes créés avec apparaîtront comme ses invités.</p>
            <div className='up-inline'>
                <label className='up-label'>Nombre</label>
                <input className='up-input sm' type='number' min='1' max='20' value={count} onChange={(e) => setCount(e.currentTarget.value)} />
                <label className='up-label'>Validité (jours, 0 = sans fin)</label>
                <input className='up-input sm' type='number' min='0' max='365' value={days} onChange={(e) => setDays(e.currentTarget.value)} />
                <button className='btn cs' onClick={create}>Créer</button>
            </div>
            {codes.length > 0 && (
                <div className='up-codes'>
                    {codes.map((c) => <code key={c}>{c}</code>)}
                    <button className='btn cw' onClick={() => copy(codes.join('\n'), notify)}><Copy size={14} /> Copier</button>
                </div>
            )}
        </Modal>
    )
}

const UnblockModal = ({ user, run, onClose }) => {
    const [clear, setClear] = useState(false)
    return (
        <Modal title='Débloquer ce compte' onClose={onClose}>
            <p className='up-text'>
                {user.email} pourra se reconnecter tout de suite. Ses échecs de pub et ses vidéos coupées (changements d'appareil) sont remis à zéro
                {user.suspended ? ' (le blocage temporaire est levé)' : ''}.
            </p>
            {user.warnings.length > 0 && (
                <label className='up-check'>
                    <input type='checkbox' checked={clear} onChange={(e) => setClear(e.currentTarget.checked)} />
                    Effacer aussi ses {user.warnings.length} avertissement(s) (sinon le prochain le rebloque)
                </label>
            )}
            <div className='up-modal-actions'>
                <button className='btn cw' onClick={onClose}>Annuler</button>
                <button className='btn cs' onClick={() => run('unblock', { clearWarnings: clear }, onClose)}>Débloquer</button>
            </div>
        </Modal>
    )
}

/* ─── Onglets de la fiche ─── */

const Logs = ({ logs }) => (
    <div className='up-table up-table-logs'>
        <div className='up-thead'><span>Événement</span><span>Date</span><span>Appareil · IP</span></div>
        {logs.map((l) => (
            <div key={l.id} className='up-row static'>
                <span>
                    <Badge tone={EVENTS[l.event]?.tone}>{EVENTS[l.event]?.label || l.event}</Badge>
                    {l.detail && <small className='up-detail'>{l.detail}</small>}
                </span>
                <span title={dateTime(l.at)}>{dateTime(l.at)}</span>
                <span><Device device={l.device} ip={l.ip} /></span>
            </div>
        ))}
        {!logs.length && <Empty>Aucun événement (le journal garde les 20 derniers)</Empty>}
    </div>
)

/** Premium donné / retiré à la main (jamais effacé) : quand, quoi, avant → après, pourquoi, par qui. */
const Gifts = ({ gifts }) => (
    <div className='up-table up-table-gifts'>
        <div className='up-thead'><span>Date</span><span>Changement</span><span>Avant → après</span><span>Raison</span><span>Par</span></div>
        {gifts.map((g) => (
            <div key={g.id} className='up-row static'>
                <span>{dateTime(g.at)}</span>
                <span>{!g.after ? <Badge tone='ko'>Retiré</Badge> : g.days ? <Badge tone={g.days > 0 ? 'gold' : 'warn'}>{g.days > 0 ? '+' : ''}{g.days} j</Badge> : <Badge tone='gold'>Date fixée</Badge>}</span>
                <span className='up-muted'>{g.before ? date(g.before) : 'gratuit'} → {g.after ? date(g.after) : 'gratuit'}</span>
                <span className='truncate' title={g.note || ''}>{g.reason === 'parrainage' ? 'Parrainage' : 'Admin'}{g.note ? ` · ${g.note}` : ''}</span>
                <span className='up-muted truncate'>{g.by || '—'}</span>
            </div>
        ))}
        {!gifts.length && <Empty>Aucun Premium donné à la main</Empty>}
    </div>
)

const Unlocks = ({ unlocks }) => (
    <div className='up-table up-table-unlocks'>
        <div className='up-thead'><span>Vidéo</span><span className='tac'>Pubs</span><span className='tac'>En</span><span>IP</span><span>Date</span></div>
        {unlocks.map((x) => (
            <div key={x.id} className='up-row static'>
                <span className='truncate' title={titleOf(x.content, x.episode)}>{titleOf(x.content, x.episode)}</span>
                <span className='tac'>{x.ads}</span>
                <span className='tac'>{duration(x.seconds)}</span>
                <span className='up-muted'>{x.ip || '—'}</span>
                <span>{dateTime(x.at)}</span>
            </div>
        ))}
        {!unlocks.length && <Empty>Aucune vidéo débloquée par les pubs</Empty>}
    </div>
)

export const WarningVideos = ({ w }) => (
    <div className='up-wvideos'>
        {(w.detail?.videos || []).map((v, i) => (
            <span key={i}>
                <b>{v.title || `#${v.contentId}`}</b>{v.episodeId ? ` (ép. #${v.episodeId})` : ''} · {dateTime(v.at)}
                {i > 0 && w.detail.gapsSeconds?.[i - 1] != null && <em> +{w.detail.gapsSeconds[i - 1]} s</em>}
            </span>
        ))}
    </div>
)

const Warnings = ({ user, pApi, notify, reload }) => {
    const [confirm, setConfirm] = useState(null)
    const del = (w) => setConfirm({
        title: 'Effacer cet avertissement',
        text: 'Il ne comptera plus pour le blocage automatique.',
        action: 'Effacer',
        onConfirm: async () => {
            try { await pApi.del(`/users/${user.id}/warnings/${w.id}`); notify('Avertissement effacé'); reload() } catch (e) { notify(e.message, true) }
        },
    })
    const delAll = () => setConfirm({
        title: 'Effacer tous ses avertissements',
        text: `Effacer les ${user.warnings.length} avertissements de ${user.email} ?`,
        action: 'Tout effacer',
        danger: true,
        onConfirm: async () => {
            try { await pApi.del(`/users/${user.id}/warnings`); notify('Avertissements effacés'); reload() } catch (e) { notify(e.message, true) }
        },
    })
    return (
        <>
            {user.warnings.length > 0 && <div className='up-right'><button className='btn up-danger-soft' onClick={delAll}><Trash2 size={14} /> Tout effacer</button></div>}
            <div className='up-table up-table-warnings'>
                <div className='up-thead'><span>Date</span><span>Vidéos ouvertes à la chaîne</span><span>IP</span><span></span></div>
                {user.warnings.map((w) => (
                    <div key={w.id} className='up-row static'>
                        <span>{dateTime(w.createdAt)}</span>
                        <WarningVideos w={w} />
                        <span className='up-muted'>{w.ip || '—'}</span>
                        <span className='tar'><button className='pay-act-btn' title='Effacer' onClick={() => del(w)}><Trash2 size={15} color='#f71717' /></button></span>
                    </div>
                ))}
                {!user.warnings.length && <Empty>Aucun avertissement</Empty>}
            </div>
            {confirm && <Confirm {...confirm} onClose={() => setConfirm(null)} />}
        </>
    )
}

const Invites = ({ user, onOpen }) => (
    <div className='up-invites'>
        <div className='up-cards'>
            <div className='up-card'>
                <p className='up-card-label'>Entré avec</p>
                <p className='up-card-value'>{user.usedCode ? <code>{user.usedCode.code}</code> : '—'}</p>
                <p className='up-card-sub'>{user.invitedBy ? <>invité par <button className='up-linkbtn' onClick={() => onOpen(user.invitedBy.id)}>{user.invitedBy.email}</button></> : 'code de l’admin'}</p>
            </div>
        </div>
        <p className='up-subtitle'>Ses invités ({user.invited.length})</p>
        <div className='up-table up-table-invited'>
            {user.invited.map((i) => (
                <div key={i.id} className='up-row' onClick={() => onOpen(i.id)}>
                    <span className='truncate'>{i.email}</span><span><Status status={i.status} /></span><span>{date(i.createdAt)}</span>
                </div>
            ))}
            {!user.invited.length && <Empty>Personne n'est entré avec ses codes</Empty>}
        </div>
        <p className='up-subtitle'>Ses codes ({user.inviteCodes.length})</p>
        <div className='up-table up-table-invited'>
            {user.inviteCodes.map((c) => (
                <div key={c.id} className='up-row static'>
                    <span><code>{c.code}</code></span>
                    <span>{c.usedBy ? <Badge tone='ok'>utilisé · {c.usedBy.email}</Badge> : c.expiresAt && new Date(c.expiresAt) < new Date() ? <Badge>expiré</Badge> : <Badge tone='info'>disponible</Badge>}</span>
                    <span className='up-muted'>{c.expiresAt ? `jusqu'au ${date(c.expiresAt)}` : 'sans fin'}</span>
                </div>
            ))}
            {!user.inviteCodes.length && <Empty>Aucun code à son nom</Empty>}
        </div>
    </div>
)

const Profiles = ({ user, pApi, notify, reload }) => {
    const [active, setActive] = useState(user.profiles[0]?.id || null)
    const [edit, setEdit] = useState(null)
    const [confirm, setConfirm] = useState(null)
    useEffect(() => { if (!user.profiles.some((p) => p.id === active)) setActive(user.profiles[0]?.id || null) }, [user.profiles, active])

    const save = async () => {
        try {
            await pApi.put(`/users/${user.id}/profiles/${edit.id}`, { name: edit.name, color: edit.color })
            notify('Profil modifié')
            setEdit(null)
            reload()
        } catch (e) { notify(e.message, true) }
    }
    const del = (p) => setConfirm({
        title: 'Supprimer ce profil',
        text: `Supprimer le profil « ${p.name} » ? Sa liste, ses notes, sa progression et son historique partent avec lui.`,
        action: 'Supprimer',
        danger: true,
        onConfirm: async () => {
            try { await pApi.del(`/users/${user.id}/profiles/${p.id}`); notify('Profil supprimé'); reload() } catch (e) { notify(e.message, true) }
        },
    })

    if (!user.profiles.length) return <Empty>Aucun profil</Empty>
    return (
        <>
            <div className='up-profiles'>
                {user.profiles.map((p) => (
                    <div key={p.id} className={`up-profile ${active === p.id ? 'on' : ''}`} onClick={() => setActive(p.id)}>
                        <span className='up-avatar' style={{ background: p.color }}>{p.name.slice(0, 1).toUpperCase()}</span>
                        <div className='up-profile-info'>
                            <p>{p.name} {user.activeProfileId === p.id && <Badge tone='live'>actif</Badge>}</p>
                            <small>{p.counts.history} vues · {p.counts.progress} en cours · {p.counts.list} en liste · {p.counts.ratings} notes</small>
                        </div>
                        <span className='up-profile-actions' onClick={(e) => e.stopPropagation()}>
                            <button className='pay-act-btn' title='Modifier' onClick={() => setEdit({ id: p.id, name: p.name, color: p.color })}><Pencil size={14} color='#fff' /></button>
                            <button className='pay-act-btn' title='Supprimer' onClick={() => del(p)}><Trash2 size={14} color='#f71717' /></button>
                        </span>
                    </div>
                ))}
            </div>
            {active && <UpProfileData key={active} pApi={pApi} userId={user.id} profileId={active} notify={notify} onChanged={reload} />}

            {edit && (
                <Modal title='Modifier le profil' onClose={() => setEdit(null)}>
                    <label className='up-label'>Nom</label>
                    <input className='up-input' maxLength={20} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.currentTarget.value })} />
                    <label className='up-label mt2'>Couleur</label>
                    <div className='up-palette'>
                        {user.palette.map((c) => (
                            <button key={c} className={edit.color === c ? 'on' : ''} style={{ background: c }} onClick={() => setEdit({ ...edit, color: c })} title={c}></button>
                        ))}
                    </div>
                    <div className='up-modal-actions'>
                        <button className='btn cw' onClick={() => setEdit(null)}>Annuler</button>
                        <button className='btn cs' disabled={!edit.name.trim()} onClick={save}>Enregistrer</button>
                    </div>
                </Modal>
            )}
            {confirm && <Confirm {...confirm} onClose={() => setConfirm(null)} />}
        </>
    )
}

/* ─── Fiche ─── */

const TABS = [
    ['profiles', 'Profils & visionnage'],
    ['logs', 'Journal'],
    ['unlocks', 'Déblocages'],
    ['gifts', 'Premium offert'],
    ['warnings', 'Avertissements'],
    ['invites', 'Invitations'],
]

/** Fiche complète d'un user (GET /users/:id) + toutes les actions. Panneau qui arrive de la droite. */
const UpUserPanel = ({ pApi, userId, onClose, onOpen, notify, onChanged }) => {
    const [user, setUser] = useState(null)
    const [error, setError] = useState('')
    const [tab, setTab] = useState('profiles')
    const [modal, setModal] = useState(null)
    const [confirm, setConfirm] = useState(null)
    const tabsRef = useWheelToPage()

    const load = useCallback(async () => {
        setError('')
        try { setUser(await pApi.get(`/users/${userId}`)) } catch (e) { setError(e.message) }
    }, [pApi, userId])
    useEffect(() => { setUser(null); load() }, [load])
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && !modal && !confirm && onClose()
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose, modal, confirm])

    const changed = () => { load(); onChanged() }

    /** Action simple (block, unblock, logout, premium…) → message + fiche rechargée. */
    const run = async (action, body = {}, after) => {
        try {
            const r = await pApi.post(`/users/${userId}/${action}`, body)
            notify(r.result || 'Fait')
            after?.()
            changed()
        } catch (e) { notify(e.message, true) }
    }

    const ask = (c) => setConfirm(c)

    return (
        <div className='up-panel-overlay' onClick={onClose}>
            <aside className='up-panel' onClick={(e) => e.stopPropagation()}>
                <button className='up-panel-x pay-act-btn' onClick={onClose} title='Fermer'><X size={20} color='#fff' /></button>
                {error && <p className='up-error'>{error}</p>}
                {!user && !error && <div className='up-loading'><Loader /></div>}
                {user && (
                    <>
                        <header className='up-panel-head'>
                            <div>
                                <h2>{user.email}</h2>
                                <div className='up-badges'>
                                    <span className='up-muted'>#{user.id}</span>
                                    <Status status={user.status} suspendedUntil={user.suspendedUntil} />
                                    {user.premium ? <Badge tone='gold'><Crown size={12} /> Premium · {date(user.premiumUntil)}</Badge> : <Badge>Gratuit</Badge>}
                                    {user.online > 0 ? <Badge tone='live'><Radio size={12} /> En ligne · {user.online} onglet{user.online > 1 ? 's' : ''}</Badge> : <Badge>Hors ligne</Badge>}
                                    {user.watching?.length > 0 && (
                                        <Badge tone='live' title={user.watching.map((w) => `${w.title} · ${w.ip || '?'}`).join('\n')}>
                                            ▶ Regarde : {user.watching[0].title}{user.watching.length > 1 ? ` (+${user.watching.length - 1})` : ''}
                                        </Badge>
                                    )}
                                    {user.watching && new Set(user.watching.map((w) => w.ip).filter(Boolean)).size > 1 && <Badge tone='warn'>Partage possible</Badge>}
                                    {user.resetPending && <Badge tone='info'>Réinitialisation en cours</Badge>}
                                    {user.support?.unread > 0 && <Badge tone='warn'><MessageCircle size={12} /> {user.support.unread} message{user.support.unread > 1 ? 's' : ''} au support</Badge>}
                                </div>
                            </div>
                        </header>

                        <div className='up-actions'>
                            {user.status === 'BLOCKED' || user.suspended ? (
                                <button className='btn cs' onClick={() => setModal('unblock')}><ShieldCheck size={15} /> Débloquer</button>
                            ) : (
                                <button className='btn up-danger-soft' onClick={() => ask({ title: 'Bloquer ce compte', text: `Bloquer ${user.email} ? Il est coupé tout de suite (vidéo arrêtée) et ne peut plus se connecter.`, action: 'Bloquer', danger: true, onConfirm: () => run('block') })}>
                                    <Ban size={15} /> Bloquer
                                </button>
                            )}
                            <button className='btn cw' onClick={() => ask({ title: 'Déconnecter', text: `Déconnecter ${user.email} de tous ses appareils ?`, action: 'Déconnecter', onConfirm: () => run('logout') })}>
                                <LogOut size={15} /> Déconnecter
                            </button>
                            <button className='btn cw' onClick={() => setModal('premium')}><Crown size={15} /> Premium</button>
                            <button className='btn cw' onClick={() => setModal('password')}><KeyRound size={15} /> Mot de passe</button>
                            <button className='btn cw' onClick={() => setModal('email')}><Mail size={15} /> Email</button>
                            <button className='btn cw' onClick={() => setModal('invites')}><Ticket size={15} /> Codes d'invitation</button>
                            {/* discussion support (PrimeStation) : ouverte à la main (les comptes bloqués y ont accès de toute façon) */}
                            {pApi.project === SUPPORT_PROJECT && <button
                                className='btn cw'
                                title={user.status === 'BLOCKED' || user.suspended ? 'Compte bloqué : il peut laisser un message, sauf si tu clôtures sa demande' : 'Il lit tes messages ; il ne répond que si tu l’y autorises'}
                                onClick={() => run('chat', { enabled: !user.support?.canChat })}
                            >
                                {user.support?.canChat
                                    ? <><MessageCircleOff size={15} /> {user.status === 'BLOCKED' || user.suspended ? 'Clôturer sa demande' : 'Ne plus l’autoriser au support'}</>
                                    : <><MessageCircle size={15} /> {user.status === 'BLOCKED' || user.suspended ? 'Rouvrir sa demande' : 'L’autoriser au support'}</>}
                            </button>}
                            {pApi.project === SUPPORT_PROJECT && (
                                <Link className='btn cw' to={`/support_plateformes?user=${user.id}`} onClick={onClose}>
                                    <MessageCircle size={15} /> {user.support?.total ? `Discussion (${user.support.total})` : 'Écrire'}
                                </Link>
                            )}
                            <button className='btn cw' onClick={() => ask({ title: 'Remettre les pubs à zéro', text: 'Ses échecs de pub d’affilée et son historique « vidéos à la chaîne » repartent de zéro.', action: 'Remettre à zéro', onConfirm: async () => { try { await pApi.post(`/users/${user.id}/reset-ads`); notify('Pubs remises à zéro'); changed() } catch (e) { notify(e.message, true) } } })}>
                                <RotateCcw size={15} /> Pubs à zéro
                            </button>
                            <button className='btn up-danger-soft' onClick={() => ask({ title: 'Supprimer le compte', text: `Tout est supprimé définitivement : profils, liste, notes, progression, historique, journal. ${user.email} est déconnecté.`, action: 'Supprimer définitivement', danger: true, confirmText: user.email, onConfirm: async () => { try { await pApi.del(`/users/${user.id}`, { confirm: user.email }); notify('Compte supprimé'); onChanged(); onClose() } catch (e) { notify(e.message, true) } } })}>
                                <UserX size={15} /> Supprimer
                            </button>
                        </div>

                        <div className='up-cards'>
                            <div className='up-card'>
                                <p className='up-card-label'>Inscrit</p>
                                <p className='up-card-value'>{date(user.createdAt)}</p>
                                <p className='up-card-sub'>{user.usedCode ? `code ${user.usedCode.code}` : '—'}{user.invitedBy ? ` · par ${user.invitedBy.email}` : ''}</p>
                            </div>
                            <div className='up-card wide'>
                                <p className='up-card-label'>Dernière connexion</p>
                                <p className='up-card-value'>{user.sessionAt ? ago(user.sessionAt) : '—'}</p>
                                <p className='up-card-sub'><Device device={user.device} ip={user.sessionIp} /></p>
                            </div>
                            <div className='up-card'>
                                <p className='up-card-label'><Clock size={12} /> Temps regardé</p>
                                <p className='up-card-value'>{duration(user.watch.seconds)}</p>
                                <p className='up-card-sub'>{user.watch.videos} vidéo{user.watch.videos > 1 ? 's' : ''} · {user.watch.finished} finie{user.watch.finished > 1 ? 's' : ''}</p>
                            </div>
                            <div className='up-card'>
                                <p className='up-card-label'><Unlock size={12} /> Pubs</p>
                                <p className='up-card-value'>{user.unlocks.length}{user.unlocks.length >= 100 ? '+' : ''} déblocage{user.unlocks.length > 1 ? 's' : ''}</p>
                                <p className='up-card-sub'>{user.adFails} échec{user.adFails > 1 ? 's' : ''} d'affilée</p>
                            </div>
                            <div className={`up-card ${user.suspended || user.switches.count >= user.switches.warnAt ? 'warn' : ''}`}>
                                {/** Vidéos coupées : connexion ailleurs pendant une lecture (seul ça bloque) ; changements d'appareil : info */}
                                <p className='up-card-label'><MonitorSmartphone size={12} /> Vidéos coupées (autre appareil)</p>
                                <p className='up-card-value'>{user.switches.count} / {user.switches.blockAt}</p>
                                <p className='up-card-sub'>
                                    {user.suspended ? `bloqué jusqu'au ${dateTime(user.suspendedUntil)}` : `sur ${user.switches.windowHours} h · bloqué ${user.switches.blockHours} h au ${user.switches.blockAt}e`}
                                </p>
                                {user.switches.devices != null && (
                                    <p className='up-card-sub'>
                                        {user.switches.devices} changement{user.switches.devices > 1 ? 's' : ''} d'appareil sur {user.switches.windowHours} h · ne bloque pas
                                    </p>
                                )}
                            </div>
                            <div className={`up-card ${user.warnings.length ? 'warn' : ''}`}>
                                <p className='up-card-label'><TriangleAlert size={12} /> Avertissements</p>
                                <p className='up-card-value'>{user.warnings.length}</p>
                                <p className='up-card-sub'>{user.warnings[0] ? `dernier ${ago(user.warnings[0].createdAt)}` : '—'}</p>
                            </div>
                        </div>

                        <nav className='up-tabs small' ref={tabsRef}>
                            {TABS.map(([id, label]) => (
                                <button key={id} className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>
                                    {label}
                                    {id === 'warnings' && user.warnings.length > 0 && <span className='up-tab-count'>{user.warnings.length}</span>}
                                    {id === 'logs' && <span className='up-tab-count muted'>{user.logs.length}</span>}
                                </button>
                            ))}
                        </nav>

                        <div className='up-panel-body'>
                            {tab === 'profiles' && <Profiles user={user} pApi={pApi} notify={notify} reload={load} />}
                            {tab === 'logs' && <Logs logs={user.logs} />}
                            {tab === 'unlocks' && <Unlocks unlocks={user.unlocks} />}
                            {tab === 'gifts' && <Gifts gifts={user.gifts || []} />}
                            {tab === 'warnings' && <Warnings user={user} pApi={pApi} notify={notify} reload={changed} />}
                            {tab === 'invites' && <Invites user={user} onOpen={onOpen} />}
                        </div>

                        {modal === 'premium' && <PremiumModal user={user} run={run} onClose={() => setModal(null)} />}
                        {modal === 'unblock' && <UnblockModal user={user} run={run} onClose={() => setModal(null)} />}
                        {modal === 'password' && <PasswordModal pApi={pApi} user={user} notify={notify} onDone={load} onClose={() => setModal(null)} />}
                        {modal === 'email' && <EmailModal pApi={pApi} user={user} notify={notify} onDone={changed} onClose={() => setModal(null)} />}
                        {modal === 'invites' && <InvitesModal pApi={pApi} user={user} notify={notify} onDone={load} onClose={() => setModal(null)} />}
                        {confirm && <Confirm {...confirm} onClose={() => setConfirm(null)} />}
                    </>
                )}
            </aside>
        </div>
    )
}

export default UpUserPanel;
